import { NavLink } from 'react-router-dom'
import { 
  LayoutDashboard, 
  CreditCard, 
  Wallet, 
  Users, 
  Settings,
  LogOut,
  Menu,
  X,
  Receipt,
  BookOpen,
  ArrowRightLeft,
  BarChart3,
  ShoppingCart,
  Send,
  UserCircle,
  FileText,
  BookMarked,
  Cog,
  ArrowUpRight,
  Activity
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useState } from 'react'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import logoImage from '../../assets/images/logo.png'

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
  { path: '/transactions', label: 'Transactions', icon: Activity },
  { path: '/payouts', label: 'Payouts', icon: ArrowUpRight },
  { path: '/fund-transfer', label: 'Fund Transfer', icon: ArrowRightLeft },
  { path: '/beneficiaries', label: 'Beneficiaries', icon: UserCircle },
  { path: '/settlements', label: 'Settlements', icon: FileText },
  { path: '/passbook', label: 'Passbook', icon: BookMarked },
  { path: '/settings', label: 'Settings', icon: Cog },
]

const Sidebar = ({ isMobileOpen, onMobileClose, isDesktopOpen = true }) => {
  const { logout } = useAuth()
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-gradient-to-b from-blue-600 via-blue-600 to-blue-700 border-r border-blue-700 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          // Mobile behavior
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${
          // Desktop behavior - always fixed, just translate
          isDesktopOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center justify-between p-6 border-b border-blue-500/30">
            <div className="flex items-center justify-center w-full">
              <div className="relative p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/20">
                <img src={logoImage} alt="LOGICLOOM Logo" className="h-12 w-auto object-contain" />
              </div>
            </div>
            <button
              onClick={onMobileClose}
              className="lg:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto scrollbar-hide">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `group flex items-center px-4 py-3 rounded-xl transition-all duration-200 relative ${
                      isActive
                        ? 'text-white font-semibold shadow-lg bg-white/20 backdrop-blur-sm'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`
                  }
                  style={({ isActive }) => {
                    if (isActive) {
                      return { 
                        backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                        color: '#ffffff',
                        transform: 'translateX(4px)',
                        backdropFilter: 'blur(8px)'
                      }
                    }
                    return { 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      backgroundColor: 'transparent' 
                    }
                  }}
                  onMouseEnter={(e) => {
                    const isActive = e.currentTarget.classList.contains('bg-white/20')
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                      e.currentTarget.style.color = '#ffffff'
                      e.currentTarget.style.transform = 'translateX(4px)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    const isActive = e.currentTarget.classList.contains('bg-white/20')
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'
                      e.currentTarget.style.transform = 'translateX(0)'
                    }
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <Icon 
                        size={20} 
                        className="mr-3 transition-all duration-200 group-hover:scale-110 flex-shrink-0"
                        strokeWidth={isActive ? 2.5 : 2}
                      />
                      <span className="text-sm flex-1 font-medium">{item.label}</span>
                      {isActive && (
                        <div className="absolute right-3 w-2 h-2 bg-white rounded-full shadow-lg"></div>
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </nav>

          {/* Logout Section */}
          <div className="p-4 border-t border-blue-500/30">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-white/90 rounded-xl hover:bg-red-500/20 hover:text-white transition-all duration-200 font-medium group border border-transparent hover:border-red-400/30"
            >
              <LogOut 
                size={20} 
                className="mr-3 transition-transform duration-200 group-hover:scale-110" 
                strokeWidth={2.5}
              />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title={null}
        size="sm"
      >
        <div className="space-y-6">
          {/* Icon Section */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="p-4 bg-red-50 rounded-full">
                <LogOut className="text-red-600" size={28} strokeWidth={2} />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-gray-900">
              Logout from LOGICLOOM?
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to logout? You'll need to login again to access your account.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 font-medium"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmLogout}
              className="flex-1 font-medium"
            >
              <LogOut className="mr-2" size={16} />
              Logout
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default Sidebar

